import { MergeCompaniesContext } from "@/services/MergeCompaniesContext/mergeCompaniesContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { DBBranchesByCompanyID } from "@/types";
import { genericFailureToast } from "@/utils/utils.util";
import { ChevronsUpDown } from "lucide-react";
import React, { useContext, useState } from "react";

// Only side is needed as a prop, so the component can determine the selected company from context
type MergeCompaniesSelectionProps = {
  side: "keep" | "remove";
};

const MergeCompaniesSelection: React.FC<MergeCompaniesSelectionProps> = ({
  side,
}) => {
  const {
    keepSideUser,
    removeSideUser,
    allCompanies,
    setKeepSideCompany,
    setRemoveSideCompany,
    keepSideCompany,
    removeSideCompany,
  } = useContext(MergeCompaniesContext);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  function handleCompanySelection(company: DBBranchesByCompanyID) {
    if (side === "keep" && company.company_id === keepSideCompany?.company_id) {
      // If the company is already selected for keep side, do not change it
      console.warn("Company is already selected for keep side.");
      return;
    }

    if (
      side === "remove" &&
      company.company_id === removeSideCompany?.company_id
    ) {
      // If the company is already selected for remove side, do not change it
      console.warn("Company is already selected for remove side.");
      return;
    }

    // Update the selected company based on the merge type
    if (side === "keep") {
      if (company.company_id === removeSideCompany?.company_id) {
        // If the selected company is the same as the remove side, do not set it for keep side
        console.warn(
          "Cannot select the same company for both sides of the merge."
        );
        genericFailureToast(
          "Cannot select the same company for both sides of the merge."
        );
        return;
      }
      setKeepSideCompany(company);
    } else {
      if (company.company_id === keepSideCompany?.company_id) {
        // If the selected company is the same as the keep side, do not set it for remove side
        console.warn(
          "Cannot select the same company for both sides of the merge."
        );
        genericFailureToast(
          "Cannot select the same company for both sides of the merge."
        );
        setRemoveSideCompany(null);
        return;
      }
      setRemoveSideCompany(company);
    }

    // Reset the search value and close the popover
    setSearchValue("");
  }

  // Restricts selectable companies to those associated with the relevant user's company_ids, so only valid companies for the merge side are shown
  const userCompanyIDs =
    side === "keep"
      ? keepSideUser?.company_ids || []
      : removeSideUser?.company_ids || [];

  // Applies both the user companyID filter and the search filter, so users only see companies they are allowed to select and can still search
  const filteredCompanies = allCompanies
    .filter((company) => userCompanyIDs.includes(company.company_id))
    .filter((company) => {
      const searchLower = searchValue.toLowerCase();
      return (
        company.company_name.toLowerCase().includes(searchLower) ||
        (company.registration_number?.toLowerCase().includes(searchLower) ??
          false)
      );
    });

  // Determines the selected company from context, so the UI always reflects the current selection for the merge side
  const selectedCompany = side === "keep" ? keepSideCompany : removeSideCompany;

  return (
    <div className="m-auto flex w-full max-w-md flex-col justify-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={
              side === "keep"
                ? !keepSideCompany
                  ? "outline"
                  : "secondary"
                : !removeSideCompany
                  ? "outline"
                  : "secondary"
            }
            aria-haspopup="listbox"
            className="relative"
            size={"lg"}
          >
            <div>
              {selectedCompany ? (
                <div className="flex flex-col items-center text-center">
                  <span>{selectedCompany.company_name}</span>
                  <span className="text-xs text-gray-300">
                    {selectedCompany.company_type === "private"
                      ? "Private Company"
                      : selectedCompany.registration_number || "No reg. number"}
                  </span>
                </div>
              ) : (
                <div className="flex text-white">
                  {side === "keep"
                    ? "Select a company to merge into"
                    : "Select a company to remove"}
                </div>
              )}
            </div>
            <ChevronsUpDown className="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-gray-300" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[350px] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search companies..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>No companies found.</CommandEmpty>
              <CommandGroup>
                {filteredCompanies
                  .sort((a, b) => a.company_name.localeCompare(b.company_name))
                  .map((company) => (
                    <CommandItem
                      key={company.company_id}
                      value={company.company_id}
                      onSelect={() => {
                        handleCompanySelection(company);
                        setOpen(false);
                        setSearchValue("");
                      }}
                      className="flex cursor-pointer flex-col items-start gap-0"
                    >
                      <div>{company.company_name}</div>
                      <div className="text-xs text-gray-500">
                        {company.company_type === "private"
                          ? "Private Company"
                          : company.registration_number || "No reg. number"}
                      </div>
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MergeCompaniesSelection;
